import { useCallback, useEffect, useMemo, useState } from "react";

import type {
    SecondFactorAuthenticationDevice,
    SecondFactorAuthenticationPolicy,
    SecondFactorAuthenticationPolicyMode,
    SecondFactorAuthenticationService,
} from "@services/SecondFactorAuthenticationService";
import type { UsersService } from "@services/UsersService";
import { TwoFactorAuthenticationAnalyticsHelper } from "@core/Analytics/TwoFactorAuthenticationAnalyticsHelper";
import { Description } from "@shared/utils/DescriptionsHelper";
import { ToastManager } from "../../../shared/designSystem/messages/ToastManager";

const fallbackTimezone = "America/Argentina/Buenos_Aires";

export interface TwoFactorAuthenticationDeviceRowOptions {
    timezone?: string;
}

export interface TwoFactorAuthenticationCollaborator {
    id: string;
    name: string;
    identification: string;
    empresa: string;
    mobilePhone: string;
}

export interface TwoFactorAuthenticationRow extends TwoFactorAuthenticationCollaborator {
    is2FAEnabled: boolean;
    isSaving: boolean;
    isDimmed: boolean;
    isSendingInvitation: boolean;
}

export interface TwoFactorAuthenticationDeviceRow {
    id: string;
    organizationID: string;
    userID: string;
    collaboratorName: string;
    deviceInstallationID: string;
    deviceLabel: string;
    platformLabel: string;
    appVersionLabel: string;
    status: string;
    statusLabel: string;
    updatedAt?: number | string;
    updatedAtLabel: string;
    createdAt?: number | string;
    createdAtLabel: string;
    deactivatedAt?: number | string;
    deactivatedAtLabel: string;
    deactivatedByLabel: string;
    deviceModel?: string;
    osVersion?: string;
    isActive: boolean;
    isSaving: boolean;
    searchValue: string;
}

interface TwoFactorAuthenticationModalViewModelInput {
    organizationID: string;
    isOpen: boolean;
    usersService: UsersService;
    secondFactorAuthenticationService: SecondFactorAuthenticationService;
    timezone?: string;
}

interface TwoFactorAuthenticationDevicesViewModelInput {
    organizationID: string;
    isOpen: boolean;
    collaborators: TwoFactorAuthenticationCollaborator[];
    secondFactorAuthenticationService: SecondFactorAuthenticationService;
    timezone?: string;
}

const fallbackPolicy: SecondFactorAuthenticationPolicy = {
    mode: "selective",
    subjectIDs: {},
};

export const normalizeSecondFactorCollaborators = (rows: any[]): TwoFactorAuthenticationCollaborator[] => {
    return rows
        .filter((row) => row?.id && !row.deactivationTimestamp)
        .map((row) => ({
            id: String(row.id),
            name: row.name ?? "",
            identification: row.identification ?? row.dni ?? "",
            empresa: row.empresa ?? "",
            mobilePhone: row.mobilePhone ?? "",
        }))
        .sort((left, right) => left.name.localeCompare(right.name, "es", { sensitivity: "base" }));
};

export const normalizeSecondFactorSearchValue = (value: string) => (
    value.toLowerCase().replace(/\s/g, "")
);

export const normalizeSecondFactorPhoneSearchValue = (value: string) => (
    value
        .replace(/^whatsapp:/i, "")
        .replace(/\D/g, "")
);

export const is2FAEnabledForSubject = (
    mode: SecondFactorAuthenticationPolicyMode,
    subjectIDs: Record<string, boolean>,
    subjectID: string,
) => {
    if (mode === "all") {
        return subjectIDs[subjectID] !== false;
    }

    return subjectIDs[subjectID] === true;
};

export const getSubjectValueForDesiredState = (
    _mode: SecondFactorAuthenticationPolicyMode,
    isEnabled: boolean,
) => isEnabled;

export const buildSecondFactorAuthenticationModePayload = (
    mode: SecondFactorAuthenticationPolicyMode,
) => ({ mode });

export const buildTwoFactorAuthenticationRows = (
    collaborators: TwoFactorAuthenticationCollaborator[],
    policy: SecondFactorAuthenticationPolicy,
    savingSubjectID?: string,
    sendingInvitationSubjectID?: string,
): TwoFactorAuthenticationRow[] => {
    return collaborators.map((collaborator) => {
        const is2FAEnabled = is2FAEnabledForSubject(
            policy.mode,
            policy.subjectIDs ?? {},
            collaborator.id,
        );

        return {
            ...collaborator,
            is2FAEnabled,
            isSaving: savingSubjectID === collaborator.id,
            isDimmed: !is2FAEnabled,
            isSendingInvitation: sendingInvitationSubjectID === collaborator.id,
        };
    });
};

export const filterTwoFactorAuthenticationRows = (
    rows: TwoFactorAuthenticationRow[],
    searchQuery: string,
) => {
    const normalizedSearchQuery = normalizeSecondFactorSearchValue(searchQuery);
    const normalizedPhoneQuery = normalizeSecondFactorPhoneSearchValue(searchQuery);

    if (!normalizedSearchQuery) {
        return rows;
    }

    return rows.filter((row) => {
        const matchesPhone = normalizedPhoneQuery.length > 0
            && normalizeSecondFactorPhoneSearchValue(row.mobilePhone).includes(normalizedPhoneQuery);

        return matchesPhone || [
            row.name,
            row.identification,
            row.empresa,
            row.mobilePhone,
        ].some((value) => normalizeSecondFactorSearchValue(value).includes(normalizedSearchQuery));
    });
};

const resolveTimestampMs = (timestamp?: number | string): number | undefined => {
    if (timestamp === undefined || timestamp === null || timestamp === "") {
        return undefined;
    }

    const numericTimestamp = typeof timestamp === "number" ? timestamp : Number(timestamp);
    const ms = Number.isFinite(numericTimestamp) ? numericTimestamp : new Date(timestamp).getTime();

    return Number.isNaN(ms) ? undefined : ms;
};

const formatLastUpdatedLabel = (updatedAt?: number | string, timezone: string = fallbackTimezone) => {
    const ms = resolveTimestampMs(updatedAt);
    if (ms === undefined) {
        return undefined;
    }

    return `Actualizado ${Description.Timestamp.date(ms, timezone)}`;
};

export const formatSecondFactorDeviceTimestamp = (
    timestamp?: number | string,
    timezone: string = fallbackTimezone,
) => {
    const ms = resolveTimestampMs(timestamp);
    if (ms === undefined) {
        return "-";
    }

    return Description.Timestamp.date(ms, timezone);
};

export const getSecondFactorDeviceStatusLabel = (status?: string) => {
    switch ((status ?? "").toUpperCase()) {
        case "ACTIVE":
            return "Activo".localized();
        case "INACTIVE":
            return "Inactivo".localized();
        default:
            return status?.trim() || "-";
    }
};

export const buildTwoFactorAuthenticationDeviceRows = (
    devices: SecondFactorAuthenticationDevice[],
    collaborators: TwoFactorAuthenticationCollaborator[],
    savingDeviceInstallationID?: string,
    options: TwoFactorAuthenticationDeviceRowOptions = {},
): TwoFactorAuthenticationDeviceRow[] => {
    const { timezone } = options;
    const collaboratorsByID = new Map(collaborators.map((collaborator) => [collaborator.id, collaborator]));

    return devices.map((device) => {
        const deviceInstallationID = device.deviceInstallationID ?? device.id;
        const collaborator = collaboratorsByID.get(device.userID);
        const collaboratorName = collaborator?.name?.trim() || device.userID || "-";
        const platformLabel = device.devicePlatform?.trim() || device.appMetadata?.platform || "-";
        const appVersionLabel = device.appVersion?.trim() || device.appMetadata?.appVersion || "-";
        const status = device.status || "";
        const statusLabel = getSecondFactorDeviceStatusLabel(status);
        const updatedAtLabel = formatSecondFactorDeviceTimestamp(device.updatedAt, timezone);
        const createdAtLabel = formatSecondFactorDeviceTimestamp(device.createdAt, timezone);
        const deactivatedAtLabel = formatSecondFactorDeviceTimestamp(device.deactivatedAt, timezone);
        const deviceModel = device.appMetadata?.deviceModel?.toString().trim() || undefined;
        const osVersion = device.appMetadata?.osVersion?.toString().trim() || undefined;
        const deviceLabel = deviceInstallationID || "-";

        // deactivatedBy is the acting user's Firebase Auth UID; the name is resolved
        // server-side (deactivatedByName) since the actor may be any user, not an admin.
        // Fall back to the raw UID only if the backend could not resolve a name.
        let deactivatedByLabel = "-";
        if (device.deactivatedBy) {
            deactivatedByLabel = device.deactivatedByName?.trim() || device.deactivatedBy;
        }

        const searchValue = normalizeSecondFactorSearchValue([
            collaboratorName,
            device.userID,
            deviceInstallationID,
            platformLabel,
            appVersionLabel,
            deviceModel ?? "",
            osVersion ?? "",
            status,
            statusLabel,
        ].join(" "));

        return {
            id: device.id ?? deviceInstallationID,
            organizationID: device.organizationID,
            userID: device.userID,
            collaboratorName,
            deviceInstallationID,
            deviceLabel,
            platformLabel,
            appVersionLabel,
            status,
            statusLabel,
            updatedAt: device.updatedAt,
            updatedAtLabel,
            createdAt: device.createdAt,
            createdAtLabel,
            deactivatedAt: device.deactivatedAt,
            deactivatedAtLabel,
            deactivatedByLabel,
            deviceModel,
            osVersion,
            isActive: status.toUpperCase() === "ACTIVE",
            isSaving: savingDeviceInstallationID === deviceInstallationID,
            searchValue,
        };
    });
};

export const filterTwoFactorAuthenticationDeviceRows = (
    rows: TwoFactorAuthenticationDeviceRow[],
    searchQuery: string,
) => {
    const normalizedSearchQuery = normalizeSecondFactorSearchValue(searchQuery);

    if (!normalizedSearchQuery) {
        return rows;
    }

    return rows.filter((row) => row.searchValue.includes(normalizedSearchQuery));
};

export type TwoFactorStatusFilter = "all" | "enabled" | "disabled";

export const useTwoFactorAuthenticationModalViewModel = ({
    organizationID,
    isOpen,
    usersService,
    secondFactorAuthenticationService,
    timezone,
}: TwoFactorAuthenticationModalViewModelInput) => {
    const [collaborators, setCollaborators] = useState<TwoFactorAuthenticationCollaborator[]>([]);
    const [policy, setPolicy] = useState<SecondFactorAuthenticationPolicy | undefined>();
    const [searchQuery, setSearchQuery] = useState("");
    const [statusFilter, setStatusFilter] = useState<TwoFactorStatusFilter>("all");
    const [isLoading, setIsLoading] = useState(false);
    const [isRefreshing, setIsRefreshing] = useState(false);
    const [isSavingMode, setIsSavingMode] = useState(false);
    const [savingSubjectID, setSavingSubjectID] = useState<string | undefined>();
    const [sendingInvitationSubjectID, setSendingInvitationSubjectID] = useState<string | undefined>();
    const [errorMessage, setErrorMessage] = useState<string | undefined>();

    const load = useCallback(async (refreshing = false) => {
        if (!isOpen) {
            return;
        }

        if (refreshing) {
            setIsRefreshing(true);
        } else {
            setIsLoading(true);
        }
        setErrorMessage(undefined);

        try {
            const [nomina, loadedPolicy] = await Promise.all([
                usersService.getNomina(organizationID),
                secondFactorAuthenticationService.getPolicy(organizationID),
            ]);

            const loadedCollaborators = normalizeSecondFactorCollaborators(Array.isArray(nomina) ? nomina : []);
            const resolvedLoadedPolicy = {
                ...fallbackPolicy,
                ...loadedPolicy,
                subjectIDs: loadedPolicy.subjectIDs ?? {},
            };

            setCollaborators(loadedCollaborators);
            setPolicy(resolvedLoadedPolicy);

            const loadedEnabledCount = loadedCollaborators.filter((collaborator) => is2FAEnabledForSubject(
                resolvedLoadedPolicy.mode,
                resolvedLoadedPolicy.subjectIDs ?? {},
                collaborator.id,
            )).length;
            const policySummary = {
                mode: resolvedLoadedPolicy.mode,
                collaboratorCount: loadedCollaborators.length,
                enabledCount: loadedEnabledCount,
                disabledCount: loadedCollaborators.length - loadedEnabledCount,
            };
            if (refreshing) {
                TwoFactorAuthenticationAnalyticsHelper.trackConfigurationRefreshed(policySummary);
            } else {
                TwoFactorAuthenticationAnalyticsHelper.trackConfigurationShown(policySummary);
            }
        } catch (error) {
            console.error(error);
            setErrorMessage("No se pudo cargar la configuracion de 2FA.".localized());
            TwoFactorAuthenticationAnalyticsHelper.trackConfigurationLoadFailed();
        } finally {
            setIsLoading(false);
            setIsRefreshing(false);
        }
    }, [isOpen, organizationID, secondFactorAuthenticationService, usersService]);

    useEffect(() => {
        if (!isOpen) {
            return;
        }

        void load(false);
    }, [isOpen, load]);

    const resolvedPolicy = policy ?? fallbackPolicy;

    const rows = useMemo(() => (
        buildTwoFactorAuthenticationRows(collaborators, resolvedPolicy, savingSubjectID, sendingInvitationSubjectID)
    ), [collaborators, resolvedPolicy, savingSubjectID, sendingInvitationSubjectID]);

    const statusFilteredRows = useMemo(() => {
        if (statusFilter === "enabled") {
            return rows.filter((row) => row.is2FAEnabled);
        }
        if (statusFilter === "disabled") {
            return rows.filter((row) => !row.is2FAEnabled);
        }
        return rows;
    }, [rows, statusFilter]);

    const visibleRows = useMemo(() => (
        filterTwoFactorAuthenticationRows(statusFilteredRows, searchQuery)
    ), [statusFilteredRows, searchQuery]);

    const enabledCount = useMemo(() => (
        rows.filter((row) => row.is2FAEnabled).length
    ), [rows]);

    const disabledCount = rows.length - enabledCount;

    const updateMode = useCallback(async (mode: SecondFactorAuthenticationPolicyMode) => {
        if (isSavingMode || resolvedPolicy.mode === mode) {
            return;
        }

        const previousPolicy = resolvedPolicy;
        const modeChangePayload = { mode, previousMode: previousPolicy.mode };
        setIsSavingMode(true);
        setPolicy({
            ...previousPolicy,
            ...buildSecondFactorAuthenticationModePayload(mode),
        });
        TwoFactorAuthenticationAnalyticsHelper.trackModeChangeSubmitted(modeChangePayload);

        try {
            const updatedPolicy = await secondFactorAuthenticationService.updateMode(organizationID, mode);
            setPolicy({
                ...fallbackPolicy,
                ...updatedPolicy,
                subjectIDs: updatedPolicy.subjectIDs ?? previousPolicy.subjectIDs ?? {},
            });
            TwoFactorAuthenticationAnalyticsHelper.trackModeChangeSucceeded(modeChangePayload);
        } catch (error) {
            console.error(error);
            setPolicy(previousPolicy);
            ToastManager.shared().showErrorMessage("No se pudo actualizar el modo de 2FA.".localized());
            TwoFactorAuthenticationAnalyticsHelper.trackModeChangeFailed(modeChangePayload);
        } finally {
            setIsSavingMode(false);
        }
    }, [isSavingMode, organizationID, resolvedPolicy, secondFactorAuthenticationService]);

    const toggleSubject = useCallback(async (subjectID: string, isEnabled: boolean) => {
        if (savingSubjectID || isSavingMode) {
            return;
        }

        const previousPolicy = resolvedPolicy;
        const value = getSubjectValueForDesiredState(previousPolicy.mode, isEnabled);
        const subjectTogglePayload = { subjectID, isEnabled, mode: previousPolicy.mode };
        setSavingSubjectID(subjectID);
        setPolicy({
            ...previousPolicy,
            subjectIDs: {
                ...previousPolicy.subjectIDs,
                [subjectID]: value,
            },
        });
        TwoFactorAuthenticationAnalyticsHelper.trackSubjectToggleSubmitted(subjectTogglePayload);

        try {
            const updatedPolicy = await secondFactorAuthenticationService.updateSubjectOverride(
                organizationID,
                subjectID,
                value,
            );
            setPolicy({
                ...fallbackPolicy,
                ...updatedPolicy,
                subjectIDs: updatedPolicy.subjectIDs ?? {},
            });
            TwoFactorAuthenticationAnalyticsHelper.trackSubjectToggleSucceeded(subjectTogglePayload);
        } catch (error) {
            console.error(error);
            setPolicy(previousPolicy);
            ToastManager.shared().showErrorMessage("No se pudo actualizar el colaborador.".localized());
            TwoFactorAuthenticationAnalyticsHelper.trackSubjectToggleFailed(subjectTogglePayload);
        } finally {
            setSavingSubjectID(undefined);
        }
    }, [isSavingMode, organizationID, resolvedPolicy, savingSubjectID, secondFactorAuthenticationService]);

    const sendInstallInvitation = useCallback(async (subjectID: string) => {
        if (sendingInvitationSubjectID) {
            return;
        }

        setSendingInvitationSubjectID(subjectID);
        TwoFactorAuthenticationAnalyticsHelper.trackInstallInvitationSubmitted({ subjectID });

        try {
            const result = await secondFactorAuthenticationService.sendInstallInvitations(
                organizationID,
                [subjectID],
            );

            if (result.failed?.length) {
                throw new Error(result.failed[0]?.error ?? "INVITATION_FAILED");
            }

            const wasSkipped = (result.skipped?.length ?? 0) > 0;
            const status = wasSkipped ? "SKIPPED_ALREADY_INSTALLED" : "ENQUEUED";
            TwoFactorAuthenticationAnalyticsHelper.trackInstallInvitationSucceeded({ subjectID, status });

            if (wasSkipped) {
                ToastManager.shared().showSuccessMessage(
                    "El colaborador ya tiene la app instalada.".localized(),
                );
            } else {
                ToastManager.shared().showSuccessMessage(
                    "Invitacion de instalacion enviada por WhatsApp.".localized(),
                );
            }
        } catch (error) {
            console.error(error);
            ToastManager.shared().showErrorMessage(
                "No se pudo enviar la invitacion de instalacion.".localized(),
            );
            TwoFactorAuthenticationAnalyticsHelper.trackInstallInvitationFailed({ subjectID });
        } finally {
            setSendingInvitationSubjectID(undefined);
        }
    }, [organizationID, secondFactorAuthenticationService, sendingInvitationSubjectID]);

    return {
        organizationID,
        mode: resolvedPolicy.mode,
        searchQuery,
        setSearchQuery,
        isLoading,
        isRefreshing,
        isSavingMode,
        errorMessage,
        rows,
        collaborators,
        visibleRows,
        totalCount: rows.length,
        statusFilter,
        setStatusFilter,
        enabledCount,
        disabledCount,
        hasSearchQuery: normalizeSecondFactorSearchValue(searchQuery).length > 0,
        lastUpdatedLabel: formatLastUpdatedLabel(resolvedPolicy.updatedAt, timezone),
        refresh: () => load(true),
        updateMode,
        toggleSubject,
        sendInstallInvitation,
    };
};

export type DeviceStatusFilter = "all" | "active" | "inactive";

export const useTwoFactorAuthenticationDevicesViewModel = ({
    organizationID,
    isOpen,
    collaborators,
    secondFactorAuthenticationService,
    timezone,
}: TwoFactorAuthenticationDevicesViewModelInput) => {
    const [devices, setDevices] = useState<SecondFactorAuthenticationDevice[]>([]);
    const [searchQuery, setSearchQuery] = useState("");
    const [statusFilter, setStatusFilter] = useState<DeviceStatusFilter>("all");
    const [isLoading, setIsLoading] = useState(false);
    const [isRefreshing, setIsRefreshing] = useState(false);
    const [savingDeviceInstallationID, setSavingDeviceInstallationID] = useState<string | undefined>();
    const [errorMessage, setErrorMessage] = useState<string | undefined>();

    const load = useCallback(async (refreshing = false) => {
        if (!isOpen) {
            return;
        }

        if (refreshing) {
            setIsRefreshing(true);
        } else {
            setIsLoading(true);
        }
        setErrorMessage(undefined);

        try {
            const loadedDevices = await secondFactorAuthenticationService.getDevices(organizationID);
            const resolvedDevices = Array.isArray(loadedDevices) ? loadedDevices : [];
            setDevices(resolvedDevices);

            const loadedActiveCount = resolvedDevices.filter((device) => (
                (device.status ?? "").toUpperCase() === "ACTIVE"
            )).length;
            const devicesSummary = {
                deviceCount: resolvedDevices.length,
                activeCount: loadedActiveCount,
                inactiveCount: resolvedDevices.length - loadedActiveCount,
            };
            if (refreshing) {
                TwoFactorAuthenticationAnalyticsHelper.trackDevicesRefreshed(devicesSummary);
            } else {
                TwoFactorAuthenticationAnalyticsHelper.trackDevicesShown(devicesSummary);
            }
        } catch (error) {
            console.error(error);
            setErrorMessage("No se pudieron cargar los dispositivos 2FA.".localized());
            TwoFactorAuthenticationAnalyticsHelper.trackDevicesLoadFailed();
        } finally {
            setIsLoading(false);
            setIsRefreshing(false);
        }
    }, [isOpen, organizationID, secondFactorAuthenticationService]);

    useEffect(() => {
        if (!isOpen) {
            return;
        }

        void load(false);
    }, [isOpen, load]);

    const rows = useMemo(() => (
        buildTwoFactorAuthenticationDeviceRows(devices, collaborators, savingDeviceInstallationID, {
            timezone,
        })
    ), [collaborators, devices, savingDeviceInstallationID, timezone]);

    const statusFilteredRows = useMemo(() => {
        if (statusFilter === "active") {
            return rows.filter((row) => row.isActive);
        }
        if (statusFilter === "inactive") {
            return rows.filter((row) => !row.isActive);
        }
        return rows;
    }, [rows, statusFilter]);

    const visibleRows = useMemo(() => (
        filterTwoFactorAuthenticationDeviceRows(statusFilteredRows, searchQuery)
    ), [statusFilteredRows, searchQuery]);

    const activeCount = useMemo(() => (
        rows.filter((row) => row.isActive).length
    ), [rows]);

    const inactiveCount = rows.length - activeCount;

    const deactivateDevice = useCallback(async (deviceInstallationID: string) => {
        if (savingDeviceInstallationID) {
            return undefined;
        }

        setSavingDeviceInstallationID(deviceInstallationID);
        TwoFactorAuthenticationAnalyticsHelper.trackDeviceDeactivationSubmitted({ deviceInstallationID });

        try {
            const updatedDevice = await secondFactorAuthenticationService.deactivateDevice(
                organizationID,
                deviceInstallationID,
            );
            setDevices((currentDevices) => currentDevices.map((device) => {
                const currentDeviceInstallationID = device.deviceInstallationID ?? device.id;
                return currentDeviceInstallationID === deviceInstallationID ? updatedDevice : device;
            }));
            TwoFactorAuthenticationAnalyticsHelper.trackDeviceDeactivationSucceeded({ deviceInstallationID });
            return updatedDevice;
        } catch (error) {
            TwoFactorAuthenticationAnalyticsHelper.trackDeviceDeactivationFailed({ deviceInstallationID });
            throw error;
        } finally {
            setSavingDeviceInstallationID(undefined);
        }
    }, [organizationID, savingDeviceInstallationID, secondFactorAuthenticationService]);

    return {
        organizationID,
        searchQuery,
        setSearchQuery,
        isLoading,
        isRefreshing,
        errorMessage,
        rows,
        visibleRows,
        totalCount: rows.length,
        statusFilter,
        setStatusFilter,
        activeCount,
        inactiveCount,
        hasSearchQuery: normalizeSecondFactorSearchValue(searchQuery).length > 0,
        refresh: () => load(true),
        deactivateDevice,
    };
};
